import { createSelector } from '@reduxjs/toolkit';
import { boolean } from 'boolean';

import { isOrganization } from 'utils/Groups';

import { getCurrentGroup, getCurrentOrg, selectGroups } from './basic';
import { getOrgStatus } from './organization';
import { getPatientGroup } from './patient';

const isSmsMessagingEnabledForGroup = createSelector(
  [selectGroups, getCurrentGroup],
  (groups, currentGroup) => {
    if (groups.length > 0) {
      if (currentGroup?.gid !== 'all') {
        return (
          currentGroup?.messagingChannel === 'mobileAndSmsMessaging' &&
          currentGroup?.patientEngageChannelEnabled === 1
        );
      } else {
        const smsMessaging = groups[1] && groups[1].messagingChannel;
        const patientEngageChannelEnabled = groups[1]?.patientEngageChannelEnabled;
        const allSameOrg = groups
          .filter((group) => group.gid !== 'all')
          .every((group) => group.messagingChannel === smsMessaging);
        if (allSameOrg) {
          return smsMessaging === 'mobileAndSmsMessaging' && patientEngageChannelEnabled === 1;
        }
      }
    }
    return false;
  }
);

const isEHRGroup = (state: IAppState): boolean => {
  const group = getCurrentGroup(state);
  let showEHR = false;
  if (group?.gid === 'all') {
    state.groups.groupInfo.data.map((group) => {
      if (group.emrSourceId) showEHR = true;
    });
  } else {
    if (group?.emrSourceId) {
      showEHR = true;
    } else {
      if (group?.parentGid === group?.gid) {
        const subgroups = state.groups.groupInfo.data.filter((x) => x.parentGid === group?.gid);
        subgroups?.map((subGroup) => {
          if (subGroup.emrSourceId) showEHR = true;
        });
      }
    }
  }
  return showEHR;
};

const hasDeviceManagementDisabled = (state: IAppState): boolean => {
  return (
    !!state.groups.groupInfo.data.find((group) => {
      return !!(group?.disableDeviceManagement && group?.disableDeviceManagement === '1');
    }) || false
  );
};

const groupHasDeviceMgmtDisabled = (state: IAppState, gid?: string): boolean => {
  const group = state.groups.groupInfo.data.find(
    (group) => group.gid === (gid || state.groups.selectedGid)
  );
  return !!(group?.disableDeviceManagement && group?.disableDeviceManagement === '1');
};

const getGidsDeviceManagement = (state: IAppState) =>
  state.groups.groupInfo.data
    ?.filter((x) => !boolean(x.disableDeviceManagement) && x.gid !== 'all')
    .map((x) => x.gid);

const getCountryCodeByGid = (state: IAppState, gid?: string): string =>
  state.groups.groupInfo.data
    ?.find((group) => group.gid === (gid || state.groups.selectedGid))
    ?.groupCountry?.substr(-3) || 'USA'; // TODO: remove in RC 21.10 and use iso formatter

const getGroupDisclaimer = createSelector(
  [selectGroups, getCurrentGroup],
  (groups, currentGroup) => {
    if (groups.length > 0) {
      if (currentGroup?.groupDisclaimer) return currentGroup.groupDisclaimer;
      else {
        const orgGid = (groups[1] && groups[1].parentGid) || '';
        const allSameOrg = groups
          .filter((group) => group.gid !== 'all')
          .every((group) => group.parentGid === orgGid);
        if (allSameOrg) return groups[1].groupDisclaimer || '';
      }
    }
  }
);

const getDisclaimerSize = createSelector(
  [getGroupDisclaimer, getOrgStatus],
  (groupDisclaimer, orgStatus) => {
    if (groupDisclaimer && orgStatus) {
      return 2;
    } else if (groupDisclaimer || orgStatus) {
      return 1;
    } else return 0;
  }
);

const isPrepaidOrg = createSelector(
  [getCurrentOrg],
  (currentOrg) => currentOrg?.commercialModel === 'prepaid'
);

/**
 * Whether the organisation that owns a particular group is prepaid.
 *
 * `isPrepaidOrg` answers it for the **sidebar selection**, which is the wrong question wherever
 * the screen is about a specific group rather than the one currently highlighted. With "All
 * groups" selected it resolves to the synthetic `all` row, which has no `commercialModel`; for a
 * clinician whose access is subgroups only, `getCurrentOrg` finds no parent and returns undefined.
 * Both answer "not prepaid" for an organisation that is, and the order review then shows no price
 * on an order the credit gate will price and can refuse (dev01, 2026-09-15).
 */
const isPrepaidOrgForGid = (state: IAppState, gid?: string | null): boolean => {
  if (!gid) return false;
  const groups = selectGroups(state);
  const group = groups.find((candidate) => candidate.gid === gid);
  if (!group) return false;
  const org = isOrganization(group)
    ? group
    : groups.find((candidate) => candidate.gid === group.parentGid);
  return org?.commercialModel === 'prepaid';
};

const getGroupTZ = createSelector([getPatientGroup], (group) => {
  return group?.groupTimezone || 'America/Toronto';
});

export {
  isSmsMessagingEnabledForGroup,
  isEHRGroup,
  hasDeviceManagementDisabled,
  groupHasDeviceMgmtDisabled,
  getGidsDeviceManagement,
  getCountryCodeByGid,
  getGroupDisclaimer,
  getDisclaimerSize,
  getGroupTZ,
  isPrepaidOrg,
  isPrepaidOrgForGid
};
